import { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import i18n from 'i18n';
import { useApplicationContext } from 'contexts/ApplicationContext';
import InternalTransactionPartyControl from './components/InternalTransactionPartyControl';
import ExternalTransactionPartyControl from './components/ExternalTransactionPartyControl';
import { fromErrorDto, isConveraOutgoing, toDto } from './components/PaymentTransactionModel';
import TransitTransactionAsyncSelect from './components/TransitTransactionAsyncSelect';
import CommissionsList from './components/CommissionsList';
import {
  AMOUNT_INPUT_VALUE_TYPE,
  MAX_TRANSACTION_COMMENT_LENGTH,
  MAX_UPLOADED_FILES_SIZE,
  PAYMENT_METHOD,
  PAYMENT_PROVIDER,
  TRANSACTION_PARTY_TYPE,
  TRANSACTION_TYPES,
  TRANSACTION_REQUISITES_TAB,
  ERROR_CODES,
  TRANSFER_TYPE,
  CUSTOM_TABS_DIRECTION
} from 'components/constants';
import i18nContext from 'components/i18n-context';
import CustomTabs from 'components/CustomTabs';
import Loader from 'components/Loader';
import { convertBytesToMegabytes, prepareFieldErrors, regularFieldRegExp } from 'services/utils';
import { ROUTE_PATHS } from 'routes/constants';
import { calculateTransactionCommission } from 'services/requestAgent';
import { AttachDoc } from 'uikit/AttachDoc/AttachDoc';
import AmountInput from 'uikit/AmountInput';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import { CustomDatePicker } from 'uikit/CustomDatePicker';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import { InputDropDownSearch } from 'uikit/InputDropDown/InputDropDownSearch';
import './PaymentTransaction.scss';

const TRANSIT_TRANSACTION_DETAILS_TABS = [
  {
    name: 'externalIdAndProviderCreatedAt',
    title: i18n.getMessage('createTransaction.transitTransactionsDetails.tab.externalIdAndProviderCreatedAt'),
    icon: null
  },
  {
    name: 'transitTransactionId',
    title: i18n.getMessage('createTransaction.transitTransactionsDetails.tab.transitTransactionId'),
    icon: null
  }
];

const PaymentTransaction = ({ transactionsStore }) => {
  const i18n = useContext(i18nContext);
  const { constants } = useApplicationContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const transitTransactionId = searchParams.get('transitTransactionId') || null;
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [activeTab, setActiveTab] = useState(TRANSIT_TRANSACTION_DETAILS_TABS[1].name);
  const serverFieldsErrors = prepareFieldErrors(i18n, transactionsStore.error);

  const form = useFormik({
    validateOnChange: false,
    enableReinitialize: true,
    initialValues: {
      transactionType: TRANSACTION_TYPES.INCOMING,
      amount: '',
      commissions: [
        {
          tariffId: null,
          amount: '',
          currency: 'EUR',
          label: 'Custom Commission',
          isCustom: true
        }
      ],
      currency: '',
      paymentMethod: '',
      comment: '',
      commentToAdmin: '',
      senderAccount: null,
      recipientAccount: null,
      paymentProvider: '',
      partyType: TRANSACTION_PARTY_TYPE.COMPANY,
      partyFirstName: '',
      partyLastName: '',
      partyCompanyName: '',
      activePartyTab: TRANSACTION_REQUISITES_TAB.IBAN,
      iban: '',
      accountNumber: '',
      sortCode: '',
      bankCountry: null,
      bankName: '',
      bic: '',
      registrationNumber: null,
      partyCity: null,
      partyCountry: null,
      partyState: null,
      partyPostCode: null,
      partyAddress: null,
      filename: '',
      transitTransaction: null,
      externalId: null,
      providerCreatedAt: null,
      documents: [],
      payment_purpose: null
    },
    validationSchema: Yup.object({
      paymentProvider: Yup.string().required(i18n.getMessage('createTransaction.validation.isRequired')),
      paymentMethod: Yup.string().required(i18n.getMessage('createTransaction.validation.isRequired')),
      senderAccount: Yup.object().when(['transactionType', 'paymentMethod'], {
        is: (transactionType, paymentMethod) =>
          transactionType === TRANSACTION_TYPES.OUTGOING ||
          (transactionType === TRANSACTION_TYPES.INCOMING && paymentMethod === PAYMENT_METHOD.INTERNAL),
        then: () => Yup.object().required(i18n.getMessage('createTransaction.validation.isRequired')),
        otherwise: () => Yup.object().nullable().notRequired()
      }),
      recipientAccount: Yup.object().when(['transactionType', 'paymentMethod'], {
        is: (transactionType, paymentMethod) =>
          transactionType === TRANSACTION_TYPES.INCOMING ||
          (transactionType === TRANSACTION_TYPES.OUTGOING && paymentMethod === PAYMENT_METHOD.INTERNAL),
        then: () => Yup.object().required(i18n.getMessage('createTransaction.validation.isRequired')),
        otherwise: () => Yup.object().nullable().notRequired()
      }),
      partyFirstName: Yup.string().when(['partyType', 'paymentMethod'], {
        is: (partyType, paymentMethod) =>
          partyType === TRANSACTION_PARTY_TYPE.INDIVIDUAL && paymentMethod !== PAYMENT_METHOD.INTERNAL,
        then: () => Yup.string().required(i18n.getMessage('createTransaction.validation.isRequired')),
        otherwise: () => Yup.string().nullable().notRequired()
      }),
      partyLastName: Yup.string().when(['partyType', 'paymentMethod'], {
        is: (partyType, paymentMethod) =>
          partyType === TRANSACTION_PARTY_TYPE.INDIVIDUAL && paymentMethod !== PAYMENT_METHOD.INTERNAL,
        then: () => Yup.string().required(i18n.getMessage('createTransaction.validation.isRequired')),
        otherwise: () => Yup.string().nullable().notRequired()
      }),
      partyCompanyName: Yup.string().when(['partyType', 'paymentMethod'], {
        is: (partyType, paymentMethod) =>
          partyType === TRANSACTION_PARTY_TYPE.COMPANY && paymentMethod !== PAYMENT_METHOD.INTERNAL,
        then: () => Yup.string().required(i18n.getMessage('createTransaction.validation.isRequired')),
        otherwise: () => Yup.string().nullable().notRequired()
      }),
      partyCountry: Yup.string()
        .when('paymentMethod', {
          is: (paymentMethod) => paymentMethod === PAYMENT_METHOD.SWIFT || paymentMethod === PAYMENT_METHOD.TARGET2,
          then: () => Yup.string().required(i18n.getMessage('createTransaction.validation.isRequired')),
          otherwise: () => Yup.string().nullable().notRequired()
        })
        .when(['transactionType', 'paymentProvider'], {
          is: isConveraOutgoing,
          then: (schema) =>
            schema.matches(regularFieldRegExp, i18n.getMessage('createTransaction.validation.invalidFormat'))
        }),
      partyCity: Yup.string().when(['transactionType', 'paymentProvider'], {
        is: isConveraOutgoing,
        then: (schema) =>
          schema.nullable().matches(regularFieldRegExp, i18n.getMessage('createTransaction.validation.invalidFormat')),
        otherwise: (schema) => schema.nullable().notRequired()
      }),
      partyPostCode: Yup.string().when(['transactionType', 'paymentProvider'], {
        is: isConveraOutgoing,
        then: (schema) =>
          schema.nullable().matches(regularFieldRegExp, i18n.getMessage('createTransaction.validation.invalidFormat')),
        otherwise: (schema) => schema.nullable().notRequired()
      }),
      partyAddress: Yup.string().when(['transactionType', 'paymentProvider'], {
        is: isConveraOutgoing,
        then: (schema) =>
          schema.nullable().matches(regularFieldRegExp, i18n.getMessage('createTransaction.validation.invalidFormat')),
        otherwise: (schema) => schema.nullable().notRequired()
      }),
      partyState: Yup.string().when(['transactionType', 'paymentProvider'], {
        is: isConveraOutgoing,
        then: (schema) =>
          schema
            .nullable()
            .matches(regularFieldRegExp, i18n.getMessage('createTransaction.validation.invalidFormat'))
            .max(100, i18n.getMessage('createTransaction.validation.maxSize', { size: 100 })),
        otherwise: (schema) => schema.nullable().notRequired()
      }),
      iban: Yup.string().when(['activePartyTab', 'paymentMethod'], {
        is: (activePartyTab, paymentMethod) =>
          activePartyTab === TRANSACTION_REQUISITES_TAB.IBAN && paymentMethod !== PAYMENT_METHOD.INTERNAL,
        then: () => Yup.string().required(i18n.getMessage('createTransaction.validation.isRequired')),
        otherwise: () => Yup.string().nullable().notRequired()
      }),
      transitTransaction: Yup.object().when(['paymentMethod', 'transactionType'], {
        is: (paymentMethod, transactionType) =>
          paymentMethod !== PAYMENT_METHOD.INTERNAL &&
          activeTab === 'transitTransactionId' &&
          transactionType === TRANSACTION_TYPES.INCOMING,
        then: () => Yup.object().required(i18n.getMessage('createTransaction.validation.isRequired')),
        otherwise: () => Yup.object().nullable()
      }),
      externalId: Yup.string().nullable(),
      providerCreatedAt: Yup.string().when(['paymentMethod', 'transactionType'], {
        is: (paymentMethod, transactionType) =>
          paymentMethod !== PAYMENT_METHOD.INTERNAL &&
          activeTab === 'externalIdAndProviderCreatedAt' &&
          transactionType === TRANSACTION_TYPES.INCOMING,
        then: () => Yup.string().required(i18n.getMessage('createTransaction.validation.isRequired')),
        otherwise: () => Yup.string().nullable()
      }),
      accountNumber: Yup.string().when(['activePartyTab', 'paymentMethod'], {
        is: (activePartyTab, paymentMethod) =>
          (activePartyTab === TRANSACTION_REQUISITES_TAB.LOCAL_BANK_ACCOUNT ||
            activePartyTab === TRANSACTION_REQUISITES_TAB.NON_IBAN_BANK_COUNTRIES) &&
          paymentMethod !== PAYMENT_METHOD.INTERNAL,
        then: () => Yup.string().required(i18n.getMessage('createTransaction.validation.isRequired')),
        otherwise: () => Yup.string().nullable().notRequired()
      }),
      sortCode: Yup.string().when(['activePartyTab', 'paymentMethod'], {
        is: (activePartyTab, paymentMethod) =>
          activePartyTab === TRANSACTION_REQUISITES_TAB.LOCAL_BANK_ACCOUNT && paymentMethod !== PAYMENT_METHOD.INTERNAL,
        then: () => Yup.string().required(i18n.getMessage('createTransaction.validation.isRequired')),
        otherwise: () => Yup.string().nullable().notRequired()
      }),
      bankCountry: Yup.string().when(['activePartyTab', 'paymentMethod'], {
        is: (activePartyTab, paymentMethod) =>
          activePartyTab === TRANSACTION_REQUISITES_TAB.NON_IBAN_BANK_COUNTRIES &&
          paymentMethod !== PAYMENT_METHOD.INTERNAL,
        then: () => Yup.string().required(i18n.getMessage('createTransaction.validation.isRequired')),
        otherwise: () => Yup.string().nullable().notRequired()
      }),
      bankName: Yup.string().when(['activePartyTab', 'paymentMethod'], {
        is: (activePartyTab, paymentMethod) =>
          activePartyTab === TRANSACTION_REQUISITES_TAB.NON_IBAN_BANK_COUNTRIES &&
          paymentMethod !== PAYMENT_METHOD.INTERNAL,
        then: () => Yup.string().required(i18n.getMessage('createTransaction.validation.isRequired')),
        otherwise: () => Yup.string().nullable().notRequired()
      }),
      bic: Yup.string().when(['activePartyTab', 'paymentMethod'], {
        is: (activePartyTab, paymentMethod) =>
          activePartyTab === TRANSACTION_REQUISITES_TAB.NON_IBAN_BANK_COUNTRIES &&
          paymentMethod !== PAYMENT_METHOD.INTERNAL,
        then: () => Yup.string().required(i18n.getMessage('createTransaction.validation.isRequired')),
        otherwise: () => Yup.string().nullable().notRequired()
      }),
      amount: Yup.string().required(i18n.getMessage('createTransaction.validation.isRequired')),
      commissions: Yup.array().of(
        Yup.object().shape({
          tariffId: Yup.number().nullable().positive(i18n.getMessage('createTransaction.validation.mustBePositive')),
          amount: Yup.string()
            .nullable()
            .test('is-positive', i18n.getMessage('createTransaction.validation.mustBePositive'), function (value) {
              if (!value || value === '') return true; // Allow empty values
              const num = parseFloat(value);
              return !isNaN(num) && num > 0;
            })
        })
      ),
      currency: Yup.string().required(i18n.getMessage('createTransaction.validation.isRequired')),
      comment: Yup.string()
        .required(i18n.getMessage('createTransaction.validation.isRequired'))
        .max(
          MAX_TRANSACTION_COMMENT_LENGTH,
          i18n.getMessage('createTransaction.validation.maxSize', { size: MAX_TRANSACTION_COMMENT_LENGTH })
        ),
      commentToAdmin: Yup.string()
        .nullable()
        .max(
          MAX_TRANSACTION_COMMENT_LENGTH,
          i18n.getMessage('createTransaction.validation.maxSize', { size: MAX_TRANSACTION_COMMENT_LENGTH })
        ),
      payment_purpose: Yup.string().when(['transactionType', 'paymentProvider', 'bankCountry'], {
        is: (transactionType, paymentProvider, bankCountry) => {
          if (transactionType !== TRANSACTION_TYPES.OUTGOING) {
            return false;
          }
          if (!bankCountry) {
            return false;
          }

          // Get payment purposes from constants to check if this combination is supported
          const paymentPurposes = constants?.paymentPurposes || [];
          const providerPurposes = paymentPurposes.find((item) => item.transferProvider === paymentProvider);

          // Check if provider has purposes for this specific country
          if (!providerPurposes || !providerPurposes.paymentPurposes) {
            return false;
          }

          const countryPurposes = providerPurposes.paymentPurposes[bankCountry] || {};
          return Object.keys(countryPurposes).length > 0;
        },
        then: () => Yup.string().required(i18n.getMessage('createTransaction.validation.isRequired')),
        otherwise: () => Yup.string().nullable()
      })
    }),
    onSubmit: async (values) => {
      values.transactionType === TRANSACTION_TYPES.OUTGOING
        ? await transactionsStore.createOutgoingTransaction(toDto(values))
        : await transactionsStore.createIncomingTransaction(toDto(values));
    }
  });

  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    setErrors,
    setFieldError,
    setFieldValue,
    resetForm,
    validateField
  } = form;

  useEffect(() => {
    if (transactionsStore.isNewTransactionCreated) {
      resetForm();
      transactionsStore.setIsNewTransactionCreated(false);
      navigate(`${ROUTE_PATHS.TRANSACTIONS}/${transactionsStore.newTransactionId}`);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionsStore.isNewTransactionCreated]);

  const parseServerErrors = (code, details) => {
    if (code === ERROR_CODES.ARGUMENT_NOT_VALID && details) {
      // Convert array of error details to object with field names as keys
      const fieldsObject = {};
      if (Array.isArray(details)) {
        details.forEach((detail) => {
          if (detail.field) {
            fieldsObject[detail.field] = detail.code;
          }
        });
      } else {
        // For backward compatibility, if details is already an object
        Object.assign(fieldsObject, details);
      }

      const converted = fromErrorDto(fieldsObject);
      Object.keys(converted).forEach((key) => {
        converted[key] = converted[key] ? i18n.getMessage(`error.field.${converted[key]}`) : undefined;
      });

      setErrors(converted);
    }
  };

  useEffect(() => {
    if (transactionsStore.error) {
      parseServerErrors(
        transactionsStore.error.code,
        transactionsStore.error.details || transactionsStore.error.fields
      );
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionsStore.error]);

  const transactionsTypesOptions = Object.keys(TRANSACTION_TYPES)?.map((type) => {
    return {
      key: TRANSACTION_TYPES[type],
      value: i18n.getMessage('transactionType.' + TRANSACTION_TYPES[type])
    };
  });

  useEffect(() => {
    if (values.paymentMethod && values.paymentProvider) {
      validateField('paymentProvider');
      validateField('paymentMethod');

      const providerCurrencies =
        PAYMENT_PROVIDER[values.paymentProvider].paymentMethods[values.paymentMethod]?.map((currency) => {
          return {
            key: currency,
            value: currency
          };
        }) || [];

      // If there's a transit transaction with currency, ensure it's in the list
      if (values.transitTransaction?.currency) {
        const transitCurrency = values.transitTransaction.currency;
        const currencyExists = providerCurrencies.some((option) => option.key === transitCurrency);

        if (!currencyExists) {
          // Add transit transaction currency to the list if not present
          providerCurrencies.push({ key: transitCurrency, value: transitCurrency });
        }
      }

      setCurrencyOptions(providerCurrencies);
    } else {
      setCurrencyOptions([]);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.paymentProvider, values.paymentMethod, values.transitTransaction]);

  useEffect(() => {
    if (values.recipientAccount) {
      validateField('recipientAccount');
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.recipientAccount]);

  useEffect(() => {
    if (values.senderAccount) {
      validateField('senderAccount');
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.senderAccount]);

  const handleChangeValue = (name, value) => {
    return setFieldValue(name, value);
  };

  const handleChangeTransitTransactionValue = (name, value) => {
    setFieldValue(name, value);

    // When transit transaction is selected, extract its data
    if (value) {
      // Set amount from transit transaction
      if (value.amount !== undefined && value.amount !== null) {
        setFieldValue('amount', String(value.amount));
      }

      // Set currency from transit transaction
      if (value.currency) {
        setFieldValue('currency', value.currency);

        // Ensure the currency is in the options list
        const currencyExists = currencyOptions.some((option) => option.key === value.currency);
        if (!currencyExists) {
          // Add the currency to options if it's not there
          setCurrencyOptions((prevOptions) => [...prevOptions, { key: value.currency, value: value.currency }]);
        }
      }
    } else {
      // Clear fields when transit transaction is cleared
      setFieldValue('amount', '');
      setFieldValue('currency', '');
    }
  };

  const onBlurValidate = (fieldName) => {
    validateField(fieldName);
  };

  const handleAmountBlur = async () => {
    // Remove trailing dot if present
    let cleanedAmount = values.amount;
    if (cleanedAmount && cleanedAmount.endsWith('.')) {
      cleanedAmount = cleanedAmount.slice(0, -1);
      await setFieldValue('amount', cleanedAmount);
    }

    // Validate the field
    validateField('amount');

    // Calculate commission on blur
    let account =
      values.transactionType === TRANSACTION_TYPES.INCOMING ? values.recipientAccount : values.senderAccount;

    if (cleanedAmount && values.paymentMethod && account) {
      // Determine bank country for commission calculation
      let bankCountryForCommission = values.bankCountry;

      // For incoming SWIFT, prioritize different sources of bank country
      if (values.transactionType === TRANSACTION_TYPES.INCOMING && values.paymentMethod === PAYMENT_METHOD.SWIFT) {
        // 1. Try to get from bankCountry (set by IBAN validation or manually entered in Non-IBAN tab)
        if (values.bankCountry) {
          bankCountryForCommission = values.bankCountry;
        }
        // 2. Fall back to transit transaction sender's bank country
        else if (values.transitTransaction?.sender?.bankCountry) {
          bankCountryForCommission = values.transitTransaction.sender.bankCountry;
        }
      }

      let data = {
        walletNumber: account.wallet_number,
        paymentMethod: values.paymentMethod,
        transactionType: values.transactionType,
        transferType: TRANSFER_TYPE.NORMAL, // static
        amount: cleanedAmount,
        ...(bankCountryForCommission ? { bankCountry: bankCountryForCommission } : {})
      };

      try {
        let response = await calculateTransactionCommission(data);

        // Keep existing custom commissions
        const existingCustomCommissions = values.commissions?.filter((c) => !c.tariffId && c.isCustom) || [];

        // Transform backend response to include tariffId
        if (response.appliedTariffs && response.appliedTariffs.length > 0) {
          const tariffCommissions = response.appliedTariffs.map((tariff) => ({
            tariffId: tariff.tariffId,
            tariffName: tariff.systemTariffKey || `Tariff ${tariff.tariffId}`,
            amount: tariff.amount,
            currency: values.currency || 'EUR',
            feeType: tariff.feeType,
            systemTariffKey: tariff.systemTariffKey,
            highRiskCountries: tariff.highRiskCountries
          }));

          // Combine tariff-based commissions with custom ones
          await setFieldValue('commissions', [...tariffCommissions, ...existingCustomCommissions]);
        } else {
          // No tariffs from backend, keep custom commissions
          if (existingCustomCommissions.length === 0) {
            // Add default custom commission if nothing exists
            await setFieldValue('commissions', [
              {
                tariffId: null,
                amount: '',
                currency: values.currency || 'EUR',
                label: 'Custom Commission',
                isCustom: true
              }
            ]);
          }
        }
      } catch (error) {
        // Error calculating commission - keep existing custom commissions
        const existingCustomCommissions = values.commissions?.filter((c) => !c.tariffId && c.isCustom) || [];
        if (existingCustomCommissions.length === 0) {
          await setFieldValue('commissions', [
            {
              tariffId: null,
              amount: '',
              currency: values.currency || 'EUR',
              label: 'Custom Commission',
              isCustom: true
            }
          ]);
        }
      }
    }
  };

  const handleChangeTransactionType = (name, transactionType) => {
    if (transactionType !== values.transactionType) {
      resetForm();
      setFieldValue(name, transactionType);
    }
  };

  useEffect(() => {
    setFieldValue('documents', transactionsStore.uploadedTransactionFiles);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionsStore.uploadedTransactionFiles]);

  // Set today's date as default for providerCreatedAt when no transit transaction
  useEffect(() => {
    if (
      values.transactionType === TRANSACTION_TYPES.INCOMING &&
      values.paymentMethod !== PAYMENT_METHOD.INTERNAL &&
      activeTab === 'externalIdAndProviderCreatedAt' &&
      !values.transitTransaction &&
      !values.providerCreatedAt
    ) {
      // Set today's date as default
      setFieldValue('providerCreatedAt', moment().format('DD/MM/YYYY'));
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.transactionType, values.paymentMethod, activeTab, values.transitTransaction]);

  // Add account currency to options when account is selected
  useEffect(() => {
    // For INCOMING transaction with recipientAccount
    if (values.transactionType === TRANSACTION_TYPES.INCOMING && values.recipientAccount?.currency) {
      const currency = values.recipientAccount.currency;
      setCurrencyOptions((prevOptions) => {
        const exists = prevOptions.some((opt) => opt.key === currency);
        if (!exists) {
          return [...prevOptions, { key: currency, value: currency }];
        }
        return prevOptions;
      });
    }

    // For OUTGOING transaction with senderAccount
    if (values.transactionType === TRANSACTION_TYPES.OUTGOING && values.senderAccount?.currency) {
      const currency = values.senderAccount.currency;
      setCurrencyOptions((prevOptions) => {
        const exists = prevOptions.some((opt) => opt.key === currency);
        if (!exists) {
          return [...prevOptions, { key: currency, value: currency }];
        }
        return prevOptions;
      });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.recipientAccount, values.senderAccount, values.transactionType]);

  // Calculate commission when payment method or account changes
  useEffect(() => {
    const asyncOperations = async () => {
      let account =
        values.transactionType === TRANSACTION_TYPES.INCOMING ? values.recipientAccount : values.senderAccount;

      // Only calculate if we have all required fields INCLUDING amount
      // But don't trigger on amount change - that's handled by onBlur
      if (values.amount && values.paymentMethod && account) {
        // Determine bank country for commission calculation
        let bankCountryForCommission = values.bankCountry;

        // For incoming SWIFT, prioritize different sources of bank country
        if (values.transactionType === TRANSACTION_TYPES.INCOMING && values.paymentMethod === PAYMENT_METHOD.SWIFT) {
          // 1. Try to get from bankCountry (set by IBAN validation or manually entered in Non-IBAN tab)
          if (values.bankCountry) {
            bankCountryForCommission = values.bankCountry;
          }
          // 2. Fall back to transit transaction sender's bank country
          else if (values.transitTransaction?.sender?.bankCountry) {
            bankCountryForCommission = values.transitTransaction.sender.bankCountry;
          }
        }

        let data = {
          walletNumber: account.wallet_number,
          paymentMethod: values.paymentMethod,
          transactionType: values.transactionType,
          transferType: TRANSFER_TYPE.NORMAL, // static
          amount: values.amount,
          ...(bankCountryForCommission ? { bankCountry: bankCountryForCommission } : {})
        };

        try {
          let response = await calculateTransactionCommission(data);

          // Keep existing custom commissions
          const existingCustomCommissions = values.commissions?.filter((c) => !c.tariffId && c.isCustom) || [];

          // Transform backend response to include tariffId
          if (response.appliedTariffs && response.appliedTariffs.length > 0) {
            const tariffCommissions = response.appliedTariffs.map((tariff) => ({
              tariffId: tariff.tariffId,
              tariffName: tariff.systemTariffKey || `Tariff ${tariff.tariffId}`,
              amount: tariff.amount,
              currency: values.currency || 'EUR',
              feeType: tariff.feeType,
              systemTariffKey: tariff.systemTariffKey,
              highRiskCountries: tariff.highRiskCountries
            }));

            // Combine tariff-based commissions with custom ones
            await setFieldValue('commissions', [...tariffCommissions, ...existingCustomCommissions]);
          } else {
            // No tariffs from backend, keep custom commissions
            if (existingCustomCommissions.length === 0) {
              // Add default custom commission if nothing exists
              await setFieldValue('commissions', [
                {
                  tariffId: null,
                  amount: '',
                  currency: values.currency || 'EUR',
                  label: 'Custom Commission',
                  isCustom: true
                }
              ]);
            }
          }
        } catch (error) {
          // Error calculating commission - keep existing custom commissions
          const existingCustomCommissions = values.commissions?.filter((c) => !c.tariffId && c.isCustom) || [];
          if (existingCustomCommissions.length === 0) {
            await setFieldValue('commissions', [
              {
                tariffId: null,
                amount: '',
                currency: values.currency || 'EUR',
                label: 'Custom Commission',
                isCustom: true
              }
            ]);
          }
        }
      }
    };

    asyncOperations();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.paymentMethod,
    values.senderAccount,
    values.recipientAccount,
    values.transitTransaction,
    values.bankCountry
  ]);

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);

    if (tabName === 'transitTransactionId') {
      // Clear only fields related to External ID tab
      setFieldValue('externalId', null);
      setFieldValue('providerCreatedAt', null);
      setFieldError('transitTransaction', null);
      // Clear currency error as well
      setFieldError('currency', null);
    } else {
      // When switching to externalIdAndProviderCreatedAt tab
      // Only clear transit transaction, but preserve amount and other fields
      const previousTransitTransaction = values.transitTransaction;
      setFieldValue('transitTransaction', null);

      // Only clear currency if it was set by the transit transaction
      // Don't clear if currency came from account selection
      const accountCurrency =
        values.transactionType === TRANSACTION_TYPES.INCOMING
          ? values.recipientAccount?.currency
          : values.senderAccount?.currency;

      if (!accountCurrency && previousTransitTransaction) {
        // Only clear currency if it came from transit transaction
        setFieldValue('currency', '');
      }

      // Clear errors for fields that are specific to this tab
      setFieldError('externalId', null);
      setFieldError('providerCreatedAt', null);
      setFieldError('currency', null);

      // IMPORTANT: Do not clear amount - it should be preserved when switching tabs
      // Removed: setFieldValue('amount', '');
    }
  };

  return (
    <div className={'payment-transaction-wrapper'}>
      <div className={'payment-transaction-type-wrapper'}>
        <InputDropDown
          className={'payment-transaction-select-transaction-type'}
          label={i18n.getMessage('createTransaction.payment.transactionType')}
          id={'transactionType'}
          name={'transactionType'}
          value={values.transactionType}
          options={transactionsTypesOptions}
          onChange={handleChangeTransactionType}
        />
      </div>
      <form className={'payment-transaction-form'} onSubmit={handleSubmit}>
        {values.transactionType === TRANSACTION_TYPES.INCOMING && (
          <>
            <InternalTransactionPartyControl
              account={values.recipientAccount}
              paymentProvider={values.paymentProvider}
              paymentMethod={values.paymentMethod}
              setCurrencyOptions={setCurrencyOptions}
              handleChangeValue={handleChangeValue}
              property={'recipientAccount'}
              accountLabel={i18n.getMessage('createTransaction.payment.toAccount')}
              isShowPaymentMethod={true}
              isShowPaymentProvider={true}
              errors={errors}
              validateField={onBlurValidate}
            />

            {values.paymentMethod !== PAYMENT_METHOD.INTERNAL && (
              <div className={'payment-transaction-transit-transaction-details-wrapper'}>
                <CustomTabs
                  className={'payment-transaction-transit-transaction-details-tabs'}
                  tabList={TRANSIT_TRANSACTION_DETAILS_TABS}
                  direction={CUSTOM_TABS_DIRECTION.HORIZONTAL}
                  activeTab={activeTab}
                  setActiveTab={handleTabChange}
                />

                {activeTab === 'transitTransactionId' && (
                  <TransitTransactionAsyncSelect
                    value={values.transitTransaction}
                    transitTransactionId={transitTransactionId}
                    property={'transitTransaction'}
                    handleChangeValue={handleChangeTransitTransactionValue}
                    error={errors?.transitTransaction}
                  />
                )}

                {activeTab === 'externalIdAndProviderCreatedAt' && (
                  <div className={'payment-transaction-row-block'}>
                    <Input
                      className={'payment-transaction-input payment-transaction-transit-transaction-external-id'}
                      label={i18n.getMessage('createTransaction.payment.transitTransactionExternalId.label')}
                      placeholder={i18n.getMessage(
                        'createTransaction.payment.transitTransactionExternalId.placeholder'
                      )}
                      id={'externalId'}
                      name={'externalId'}
                      value={values.externalId}
                      onChange={handleChange}
                      onBlur={() => validateField('externalId')}
                      error={errors?.externalId || serverFieldsErrors?.externalId}
                    />
                    <CustomDatePicker
                      className={
                        'payment-transaction-input ' +
                        'payment-transaction-transit-transaction-provider-created-at ' +
                        'payment-transaction-date-picker'
                      }
                      label={i18n.getMessage('createTransaction.payment.providerCreatedAt')}
                      name={'providerCreatedAt'}
                      value={values.providerCreatedAt ? moment(values.providerCreatedAt, 'DD/MM/YYYY').toDate() : null}
                      onChange={(name, value) => {
                        // Convert Date object to DD/MM/YYYY format string
                        const formattedDate = value ? moment(value).format('DD/MM/YYYY') : '';
                        setFieldValue(name, formattedDate);
                      }}
                      error={errors?.providerCreatedAt || serverFieldsErrors?.providerCreatedAt}
                      maxDate={new Date()}
                      placeholderText={'dd/mm/yyyy'}
                    />
                  </div>
                )}
              </div>
            )}

            {values.paymentMethod === PAYMENT_METHOD.INTERNAL && (
              <InternalTransactionPartyControl
                account={values.senderAccount}
                paymentProvider={values.paymentProvider}
                paymentMethod={values.paymentMethod}
                handleChangeValue={handleChangeValue}
                property={'senderAccount'}
                accountLabel={i18n.getMessage('createTransaction.payment.fromAccount')}
                isShowPaymentMethod={false}
                errors={errors}
                validateField={onBlurValidate}
              />
            )}

            {values.paymentMethod !== PAYMENT_METHOD.INTERNAL && (
              <ExternalTransactionPartyControl
                data={values}
                handleChangeValue={handleChange}
                handleChangeCountryValue={handleChangeValue}
                errors={errors}
                partyLabel={i18n.getMessage('createTransaction.payment.partyLabel.sender')}
                setFieldValue={setFieldValue}
                validateField={onBlurValidate}
                serverFieldsErrors={serverFieldsErrors}
              />
            )}
          </>
        )}

        {values.transactionType === TRANSACTION_TYPES.OUTGOING && (
          <>
            <InternalTransactionPartyControl
              account={values.senderAccount}
              paymentProvider={values.paymentProvider}
              paymentMethod={values.paymentMethod}
              handleChangeValue={handleChangeValue}
              property={'senderAccount'}
              accountLabel={i18n.getMessage('createTransaction.payment.fromAccount')}
              isShowPaymentProvider={false}
              isShowPaymentMethod={true}
              validateField={onBlurValidate}
            />

            {values.paymentMethod === PAYMENT_METHOD.INTERNAL && (
              <InternalTransactionPartyControl
                account={values.recipientAccount}
                paymentProvider={values.paymentProvider}
                paymentMethod={values.paymentMethod}
                handleChangeValue={handleChangeValue}
                property={'recipientAccount'}
                accountLabel={i18n.getMessage('createTransaction.payment.toAccount')}
                isShowPaymentMethod={false}
                validateField={onBlurValidate}
              />
            )}

            {values.paymentMethod !== PAYMENT_METHOD.INTERNAL && (
              <ExternalTransactionPartyControl
                data={values}
                handleChangeValue={handleChange}
                handleChangeCountryValue={handleChangeValue}
                errors={errors}
                partyLabel={i18n.getMessage('createTransaction.payment.partyLabel.recipient')}
                setFieldValue={setFieldValue}
                validateField={onBlurValidate}
                serverFieldsErrors={serverFieldsErrors}
              />
            )}
          </>
        )}

        <div className={'payment-transaction-block-wrapper'}>
          <div className={'payment-transaction-row-block'}>
            <AmountInput
              isRequired
              className={'payment-transaction-input'}
              label={i18n.getMessage('createTransaction.payment.amount.label')}
              placeholder={i18n.getMessage('createTransaction.payment.amount.placeholder')}
              name={'amount'}
              value={values?.amount}
              returnValueType={AMOUNT_INPUT_VALUE_TYPE.STRING}
              onChange={handleChange}
              onBlur={handleAmountBlur}
              isDisabled={!!values.transitTransaction}
              error={errors?.amount}
            />
            <InputDropDownSearch
              isRequired
              className={'payment-transaction-select-currency'}
              label={i18n.getMessage('createTransaction.payment.currency')}
              id={'currency'}
              name={'currency'}
              value={values?.currency || ''}
              options={currencyOptions}
              onChange={handleChangeValue}
              onBlur={() => onBlurValidate('currency')}
              isDisabled={
                (!!values.recipientAccount && values.transactionType === TRANSACTION_TYPES.INCOMING) ||
                values.transactionType === TRANSACTION_TYPES.OUTGOING ||
                !!values.transitTransaction
              }
              error={errors?.currency}
            />
          </div>
          <CommissionsList
            commissions={values?.commissions || []}
            currency={values.currency}
            onChange={(index, value) => {
              const newCommissions = [...values.commissions];
              newCommissions[index] = { ...newCommissions[index], amount: value };
              setFieldValue('commissions', newCommissions);
            }}
            onBlur={(index) => onBlurValidate(`commissions[${index}].amount`)}
            onDelete={(index) => {
              const newCommissions = [...values.commissions];
              newCommissions.splice(index, 1);
              setFieldValue('commissions', newCommissions);
            }}
            onAdd={() => {
              const newCommissions = [...(values.commissions || [])];
              newCommissions.push({
                tariffId: null,
                amount: '',
                currency: values.currency || 'EUR',
                label: 'Custom Commission',
                isCustom: true
              });
              setFieldValue('commissions', newCommissions);
            }}
            errors={errors?.commissions}
          />

          {/* Show payment purpose field for OUTGOING transactions with supported providers and countries */}
          {(() => {
            // Check if we should show payment purpose field
            if (values.transactionType !== TRANSACTION_TYPES.OUTGOING) {
              return null;
            }

            // Get payment purposes for the current provider from constants
            const paymentPurposes = constants?.paymentPurposes || [];

            // Find purposes for current provider
            const providerPurposes = paymentPurposes.find((item) => item.transferProvider === values.paymentProvider);

            // If provider doesn't have purposes defined, don't show the field
            if (!providerPurposes || !providerPurposes.paymentPurposes) {
              return null;
            }

            // Get bank country - for OUTGOING it's the recipient's bank country
            const bankCountry = values.bankCountry || '';

            // If no bank country determined yet, don't show the field
            if (!bankCountry) {
              return null;
            }

            // Get purposes for the specific country
            const countryPurposes = providerPurposes.paymentPurposes[bankCountry] || {};

            // If no purposes for this country, don't show the field
            if (Object.keys(countryPurposes).length === 0) {
              return null;
            }

            // Format purposes as options with empty option for clearing
            const purposeOptions = [
              { key: '', value: '' }, // Empty option to allow clearing
              ...Object.entries(countryPurposes).map(([key, description]) => ({
                key: key,
                value: `${key} - ${description}`
              }))
            ];

            return (
              <InputDropDownSearch
                isRequired
                className={'internal-transaction-party-control-account'}
                label={i18n.getMessage('createTransaction.payment.paymentPurpose.label')}
                placeholder={i18n.getMessage('createTransaction.payment.paymentPurpose.placeholder')}
                id={'payment_purpose'}
                name={'payment_purpose'}
                value={values?.payment_purpose}
                options={purposeOptions}
                onChange={handleChangeValue}
                onBlur={() => onBlurValidate('payment_purpose')}
                error={errors?.payment_purpose}
              />
            );
          })()}

          <Input
            isRequired
            type={'textarea'}
            rows={2}
            className={'payment-transaction-textarea'}
            label={i18n.getMessage('createTransaction.payment.commentToTransfer')}
            id={'comment'}
            name={'comment'}
            value={values.comment}
            onChange={handleChange}
            onBlur={() => onBlurValidate('comment')}
            error={errors?.comment}
            max={MAX_TRANSACTION_COMMENT_LENGTH}
            subText={i18n.getMessage('createTransaction.payment.symbolsLeft', {
              symbolsLeft: MAX_TRANSACTION_COMMENT_LENGTH - values.comment?.length
            })}
          />

          <Input
            type={'textarea'}
            rows={2}
            className={'payment-transaction-textarea'}
            label={i18n.getMessage('createTransaction.payment.commentToAdmin')}
            id={'commentToAdmin'}
            name={'commentToAdmin'}
            value={values.commentToAdmin}
            onChange={handleChange}
            onBlur={() => onBlurValidate('commentToAdmin')}
            error={errors?.commentToAdmin}
            max={MAX_TRANSACTION_COMMENT_LENGTH}
            subText={i18n.getMessage('createTransaction.payment.symbolsLeft', {
              symbolsLeft: MAX_TRANSACTION_COMMENT_LENGTH - values.commentToAdmin?.length
            })}
          />

          <AttachDoc
            label={i18n.getMessage('createTransaction.payment.attachDocument')}
            className={'payment-transaction-attachment'}
            type={'file'}
            size={MAX_UPLOADED_FILES_SIZE}
            files={transactionsStore.uploadedTransactionFiles}
            onChange={transactionsStore.uploadTransactionAttachments}
            onRemoveFiles={transactionsStore.removeTransactionAttachment}
            isDisabled={transactionsStore.isLoading || transactionsStore.isTransactionFileUploading}
            hintText={i18n.getMessage('attachDoc.hint.text', {
              maxFilesSize: convertBytesToMegabytes(MAX_UPLOADED_FILES_SIZE)
            })}
          />
        </div>

        <div className={'payment-transaction-submit-button-wrapper'}>
          <Button
            className={'payment-transaction-submit-button'}
            type={'primary'}
            roleType={'submit'}
            size={'large'}
            onClick={() => {}}
            isDisabled={transactionsStore.isLoading}
          >
            {transactionsStore.isLoading ? (
              <Loader />
            ) : (
              i18n.getMessage('createTransaction.payment.button.createTransaction')
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

PaymentTransaction.propTypes = {
  transactionsStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  transactionsStore: stores.transactionsStore
}))(observer(PaymentTransaction));
